import { useCallback, useState } from 'react'

/**
 * Persisted UI preference (list density, collapsed sections, last tab).
 * Never used for session data — the access token lives in memory only.
 */
export const useLocalStorage = <T>(
  key: string,
  initial: T,
): [T, (next: T | ((previous: T) => T)) => void] => {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(key)
      if (raw === null) return initial
      return JSON.parse(raw) as T
    } catch {
      return initial
    }
  })

  const update = useCallback(
    (next: T | ((previous: T) => T)) => {
      setValue((previous) => {
        const resolved =
          typeof next === 'function' ? (next as (prev: T) => T)(previous) : next
        try {
          window.localStorage.setItem(key, JSON.stringify(resolved))
        } catch {
          // Storage is unavailable — the preference stays for this session only.
        }
        return resolved
      })
    },
    [key],
  )

  return [value, update]
}
